import { ref } from 'vue'
import { useContacts } from '@/composables/useContacts'
import { useCompanies } from '@/composables/useCompanies'
import { useServiceLocations } from '@/composables/useServiceLocations'
import { blankAddressDraft, type AddressDraft, type House } from '@/composables/useAddressLookup'
import { joinAddressLine, resolvedAddress } from '@/lib/address'

// The new-customer half of quick-capture. A first-time caller says who they are,
// their number and their address in ONE breath, so the form asks once and this
// owns what that costs: the API has no nested create, so one « Créer » is up to
// four sequential calls (company → its site contact → the address → the job).
//
// ★ The memo is the point, and it MATTERS MORE now than it did with one owner.
// The last call can fail (a validation the form didn't catch, a dropped
// connection) with the earlier ones already committed — and a dispatcher who
// fixes the field and presses « Créer » again must not mint a SECOND Gestion
// Chantilly *and* a second Ginette Boulanger. Each ensure* is idempotent for the
// life of the form: it returns the id it already made instead of making another.
// Abandoning the form after call 1 leaves a real customer with no job, which is a
// customer who called and is exactly what the dispatcher would have typed anyway.
export interface NewCustomerDraft {
  kind: 'contact' | 'company'
  firstName: string
  lastName: string
  phone: string
  // The SMS number, apart from `phone` because a text to a landline dies
  // silently. Always optional: no cell ⇒ this customer cannot be texted.
  cellPhone: string
  companyName: string
  companyPhone: string
  companyEmail: string
}

// The address as typed (design 170): the number as the office wrote it, the
// postal code, and the street + town for the caller without a postal code.
export type NewAddressDraft = AddressDraft
export { blankAddressDraft }

export const blankCustomerDraft = (): NewCustomerDraft => ({
  kind: 'contact',
  firstName: '',
  lastName: '',
  phone: '',
  cellPhone: '',
  companyName: '',
  companyPhone: '',
  companyEmail: '',
})

/**
 * ⭐ THE PARTIAL-PERSON RULE, in one place so the schema, the submit chain and the
 * specs cannot drift: a person exists only when BOTH names are given.
 *
 * `Contact` validates first and last name server-side, so a company branch where
 * the dispatcher typed only a phone number would 422 *after* the company was
 * already created. And on the company branch the whole person is optional — an
 * untouched group means "nobody on site", which is a legitimate answer that costs
 * the customer only the ability to be texted.
 */
export const hasPerson = (draft: NewCustomerDraft): boolean =>
  !!draft.firstName.trim() && !!draft.lastName.trim()

/**
 * The dedup answers, threaded into the chain (doc/FEATURE_CUSTOMER_DEDUP.md).
 * All optional — a form with no dedup instance (or no questions raised) runs
 * the chain exactly as before.
 */
export interface InlineCreateDedupOptions {
  // The house the lookup settled on: its spelling becomes the stored street and
  // town (the number stays as typed), and its coordinates ride WITH the address
  // so the create skips the async geocode job and lands sector-stamped in one
  // request. Null = the person chose « Créer sans localisation », or the
  // geocoder was down.
  house?: House | null
  // « Nouveau propriétaire — le retirer de sa fiche »: the claimed cell is
  // released from its holder in the same create that claims it.
  releaseConflictingCell?: boolean
  // [Utiliser ce lieu]: skip the location create and REPOINT the existing
  // address's customer slots instead (turnover — the address outlives its
  // customer). PER SLOT: only the slots this intake actually has are written.
  existingLocationId?: string | null
}

/**
 * The address a save sends, from the typed draft and the house the lookup
 * settled on (design 170). ONE place for both the intake chain and the edit
 * forms, so the stored spelling cannot drift between them.
 */
export const addressAttributes = (draft: AddressDraft, house?: House | null) => {
  const address = resolvedAddress(draft, house)
  return {
    address_line1: joinAddressLine(address.civicNumber, address.street) || null,
    city: address.city || null,
    postal_code: address.postalCode || null,
    ...(house ? { latitude: house.latitude, longitude: house.longitude } : {}),
  }
}

export function useInlineCustomerCreate() {
  const { create: createContact } = useContacts()
  const { create: createCompany } = useCompanies()
  const { create: createServiceLocation, update: updateServiceLocation } = useServiceLocations()

  const createdContactId = ref<string | null>(null)
  const createdCompanyId = ref<string | null>(null)
  const createdLocationId = ref<string | null>(null)

  // Every ensure* is silent: the form toasts the JOB, once, for the one act the
  // dispatcher performed.
  const ensureContact = async (
    draft: NewCustomerDraft,
    opts?: { releaseConflictingCell?: boolean },
  ): Promise<string | null> => {
    if (createdContactId.value) return createdContactId.value
    const created = await createContact(
      {
        first_name: draft.firstName.trim(),
        last_name: draft.lastName.trim(),
        ...(draft.phone.trim() ? { phone: draft.phone.trim() } : {}),
        // Normalized to E.164 server-side — send it as typed.
        ...(draft.cellPhone.trim() ? { cell_phone: draft.cellPhone.trim() } : {}),
        ...(opts?.releaseConflictingCell ? { release_conflicting_cell: true } : {}),
      },
      undefined,
      { silent: true },
    )
    if (!created) return null
    createdContactId.value = created.id
    return created.id
  }

  const ensureCompany = async (draft: NewCustomerDraft): Promise<string | null> => {
    if (createdCompanyId.value) return createdCompanyId.value
    const created = await createCompany(
      {
        company_name: draft.companyName.trim(),
        ...(draft.companyPhone.trim() ? { phone: draft.companyPhone.trim() } : {}),
        ...(draft.companyEmail.trim() ? { email: draft.companyEmail.trim() } : {}),
      },
      { silent: true },
    )
    if (!created) return null
    createdCompanyId.value = created.id
    return created.id
  }

  // The address block IS the customer's first service location — the whole reason
  // the new-customer case needs no second screen. Blank fields go as null (the API
  // requires none of them). With a house in hand the stored street and town are
  // the REGISTRY's spelling (his call: one address, one spelling) and the
  // coordinates ride along, so the server stamps status + sector in the same
  // request, no async job. Without one, the typed text is stored and the model
  // callback enqueues the background lookup as before.
  //
  // Both slots are written when both exist: company = who pays, contact = who is
  // on site. The server takes them in one create and 422s only if both are blank.
  const ensureLocation = async (
    slots: { contactId?: string | null; companyId?: string | null },
    draft: NewAddressDraft,
    house?: House | null,
  ): Promise<string | null> => {
    if (createdLocationId.value) return createdLocationId.value
    const created = await createServiceLocation(
      slots,
      {
        name: null,
        ...addressAttributes(draft, house),
      },
      { silent: true },
    )
    if (!created) return null
    createdLocationId.value = created.id
    return created.id
  }

  // [Utiliser ce lieu] — turnover onto a known address. PER SLOT and memoized
  // like every other step: only the slots this intake HAS are sent, so snapping
  // a company onto a known B2B address cannot wipe its site contact (the
  // CustomerSlots tri-state — an absent key is untouched).
  const repointLocation = async (
    locationId: string,
    slots: { contactId?: string | null; companyId?: string | null },
  ): Promise<string | null> => {
    if (createdLocationId.value) return createdLocationId.value
    const sparseSlots = {
      ...(slots.contactId ? { contactId: slots.contactId } : {}),
      ...(slots.companyId ? { companyId: slots.companyId } : {}),
    }
    const updated = await updateServiceLocation(locationId, {}, sparseSlots, { silent: true })
    if (!updated) return null
    createdLocationId.value = locationId
    return locationId
  }

  /**
   * The whole chain for one « Créer », in the order the records depend on each
   * other. Returns the location id the job will point at, or null if any step
   * failed — the caller bails, and a retry resumes from where it stopped.
   */
  const ensureCustomerLocation = async (
    customer: NewCustomerDraft,
    address: NewAddressDraft,
    dedup?: InlineCreateDedupOptions,
  ): Promise<string | null> => {
    let contactId: string | null = null
    let companyId: string | null = null
    const release = { releaseConflictingCell: dedup?.releaseConflictingCell }

    if (customer.kind === 'company') {
      companyId = await ensureCompany(customer)
      if (!companyId) return null
      // The personne-ressource, if the dispatcher got one. Optional by ruling: no
      // person just means this customer cannot be texted.
      if (hasPerson(customer)) {
        contactId = await ensureContact(customer, release)
        if (!contactId) return null
      }
    } else {
      contactId = await ensureContact(customer, release)
      if (!contactId) return null
    }

    if (dedup?.existingLocationId) {
      return repointLocation(dedup.existingLocationId, { contactId, companyId })
    }
    return ensureLocation({ contactId, companyId }, address, dedup?.house)
  }

  // Only on a CLOSED conversation (submit succeeded, or the modal was dismissed
  // and wiped). Never between retries — that is what the memo is protecting.
  const resetInlineCreate = () => {
    createdContactId.value = null
    createdCompanyId.value = null
    createdLocationId.value = null
  }

  return {
    ensureContact, ensureCompany, ensureLocation, repointLocation, ensureCustomerLocation,
    resetInlineCreate, createdContactId, createdCompanyId, createdLocationId,
  }
}
